/**
 * Native Web Audio Synthesizer
 * Zero external audio files, zero network bandwidth, zero decoding latency.
 */
class SoundEngine {
  constructor() {
    this.ctx = null;
    this.unlocked = false;
    if (typeof window !== 'undefined') {
      this.initListeners();
    }
  }

  getContext() {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        this.ctx = new AudioContextClass();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  initListeners() {
    const unlock = () => {
      const ctx = this.getContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().then(() => { this.unlocked = true; }).catch(() => {});
      } else if (ctx && ctx.state === 'running') {
        this.unlocked = true;
      }
      if (this.unlocked) {
        window.removeEventListener('pointerdown', unlock, true);
        window.removeEventListener('keydown', unlock, true);
      }
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
  }

  /**
   * Play a clean sine beep tone
   * @param {number} freq Frequency in Hz
   * @param {number} duration Duration in seconds
   * @param {number} volume Volume 0 to 1
   */
  beep(freq = 600, duration = 0.08, volume = 0.15) {
    try {
      const ctx = this.getContext();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);

      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch {
      // Audio failure must fail silently
    }
  }

  playSuccess() {
    this.beep(523.25, 0.06, 0.12); // C5
    setTimeout(() => this.beep(659.25, 0.10, 0.15), 60); // E5
  }

  playError() {
    this.beep(220, 0.12, 0.15); // A3
  }

  playKeyClick(isError = false) {
    if (this.muted) return;
    try {
      const ctx = this.getContext();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const now = ctx.currentTime;
      osc.type = isError ? 'triangle' : 'sine';
      osc.frequency.setValueAtTime(isError ? 160 : 780, now);
      osc.frequency.exponentialRampToValueAtTime(isError ? 80 : 220, now + 0.02);
      gain.gain.setValueAtTime(isError ? 0.2 : 0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.025);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.025);
    } catch {}
  }

  /**
   * Synthesize realistic mechanical switch sounds
   * @param {'clicky'|'linear'|'tactile'|'beep'|'mute'} profile
   */
  playSwitchSound(profile = 'clicky') {
    if (this.muted || profile === 'mute') return;
    try {
      const ctx = this.getContext();
      if (!ctx) return;
      const now = ctx.currentTime;
      if (profile === 'clicky') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(1400, now);
        osc.frequency.exponentialRampToValueAtTime(320, now + 0.018);
        gain.gain.setValueAtTime(0.18, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.022);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.022);
      } else if (profile === 'linear') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(110, now + 0.035);
        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.04);
      } else if (profile === 'tactile') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(620, now);
        osc.frequency.exponentialRampToValueAtTime(220, now + 0.026);
        gain.gain.setValueAtTime(0.14, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.03);
      } else {
        this.beep(520, 0.03, 0.08);
      }
    } catch {}
  }
}

export const soundEngine = new SoundEngine();
